<template>
  <div class="pitchcrafter-proposals">
    <v-container fluid class="px-6 py-4">
      <!-- Header Section -->
      <div class="header-section mb-6">
        <div class="header-content">
          <h1 class="page-title">
            <v-icon large class="mr-3" color="#667eea">mdi-file-document-outline</v-icon>
            Proposal Management
          </h1>
          <v-btn
            color="primary"
            large
            elevation="2"
            @click="showCreateDialog = true"
            class="create-btn"
          >
            <v-icon left>mdi-plus</v-icon>
            Create New Proposal
          </v-btn>
        </div>
      </div>

      <!-- Filters Section -->
      <v-card class="filters-card mb-6" elevation="4">
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="filters.search"
                label="Search proposals..."
                prepend-inner-icon="mdi-magnify"
                outlined
                dense
                clearable
                @input="applyFilters"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.status"
                label="Status"
                :items="statusOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.type"
                label="Type"
                :items="typeOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                v-model="filters.industry"
                label="Industry"
                :items="industryOptions"
                outlined
                dense
                clearable
                @change="applyFilters"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Proposals Table -->
      <v-card elevation="4">
        <v-data-table
          :headers="headers"
          :items="proposals"
          :loading="isLoading"
          :search="filters.search"
          class="proposals-table"
          :items-per-page="10"
          :footer-props="{
            'items-per-page-options': [10, 25, 50]
          }"
        >
          <!-- Client Name -->
          <template v-slot:[`item.client_name`]="{ item }">
            <div class="client-info">
              <div class="client-avatar">
                {{ item.client_name.charAt(0).toUpperCase() }}
              </div>
              <div class="client-details">
                <div class="client-name">{{ item.client_name }}</div>
                <div class="client-company">{{ item.client_company }}</div>
              </div>
            </div>
          </template>

          <!-- Status -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              small
              class="status-chip"
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>

          <!-- Type -->
          <template v-slot:[`item.proposal_type`]="{ item }">
            <div class="type-badge">
              <v-icon :color="getTypeColor(item.proposal_type)" class="mr-1">
                {{ getTypeIcon(item.proposal_type) }}
              </v-icon>
              {{ getTypeText(item.proposal_type) }}
            </div>
          </template>

          <!-- Value -->
          <template v-slot:[`item.opportunity_size`]="{ item }">
            <div class="opportunity-value">
              {{ formatCurrency(item.opportunity_size) }}
            </div>
          </template>

          <!-- Created Date -->
          <template v-slot:[`item.created_at`]="{ item }">
            <div class="date-info">
              {{ formatDate(item.created_at) }}
            </div>
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="action-buttons">
              <v-btn
                v-if="item.status === 'draft'"
                icon
                small
                color="success"
                @click="generatePitch(item)"
                :disabled="generatingPitch === item.id"
              >
                <v-icon>mdi-sparkles</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="primary"
                @click="viewProposal(item)"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="warning"
                @click="editProposal(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="error"
                @click="deleteProposal(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- Create Proposal Dialog -->
      <v-dialog v-model="showCreateDialog" max-width="800px" persistent>
        <v-card>
          <v-card-title class="headline">
            <v-icon left color="primary">mdi-file-document-plus</v-icon>
            Create New Proposal
          </v-card-title>
          <v-card-text class="pa-6">
            <v-form ref="createForm" v-model="validForm">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newProposal.client_name"
                    label="Client Name"
                    outlined
                    required
                    :rules="[v => !!v || 'Client name is required']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newProposal.client_company"
                    label="Client Company"
                    outlined
                    required
                    :rules="[v => !!v || 'Company name is required']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newProposal.client_email"
                    label="Client Email"
                    outlined
                    type="email"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newProposal.industry"
                    label="Industry"
                    outlined
                    required
                    :rules="[v => !!v || 'Industry is required']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newProposal.opportunity_size"
                    label="Opportunity Size"
                    outlined
                    prefix="$"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newProposal.buyer_persona"
                    label="Buyer Persona"
                    :items="buyerPersonaOptions"
                    outlined
                    required
                    :rules="[v => !!v || 'Buyer persona is required']"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="newProposal.proposal_type"
                    label="Proposal Type"
                    :items="typeOptions"
                    outlined
                    required
                    :rules="[v => !!v || 'Proposal type is required']"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="newProposal.pain_points"
                    label="Client Pain Points"
                    outlined
                    rows="3"
                    hint="Describe the client's challenges and pain points"
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="newProposal.requirements"
                    label="Requirements"
                    outlined
                    rows="3"
                    hint="Specific requirements and expectations"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn text @click="closeCreateDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              :loading="creatingProposal"
              :disabled="!validForm"
              @click="createProposal"
            >
              Create Proposal
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PitchCrafterAIProposals",
  data() {
    return {
      isLoading: false,
      proposals: [],
      showCreateDialog: false,
      validForm: false,
      creatingProposal: false,
      generatingPitch: null,
      filters: {
        search: '',
        status: '',
        type: '',
        industry: ''
      },
      newProposal: {
        client_name: '',
        client_company: '',
        client_email: '',
        industry: '',
        opportunity_size: '',
        pain_points: '',
        requirements: '',
        buyer_persona: 'executive',
        proposal_type: 'email'
      },
      headers: [
        { text: 'Client', value: 'client_name', sortable: true },
        { text: 'Industry', value: 'industry', sortable: true },
        { text: 'Type', value: 'proposal_type', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Value', value: 'opportunity_size', sortable: true },
        { text: 'Created', value: 'created_at', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, width: '150px' }
      ],
      statusOptions: [
        { text: 'Draft', value: 'draft' },
        { text: 'Generated', value: 'generated' },
        { text: 'Sent', value: 'sent' },
        { text: 'Accepted', value: 'accepted' },
        { text: 'Rejected', value: 'rejected' }
      ],
      typeOptions: [
        { text: 'Email', value: 'email' },
        { text: 'Presentation', value: 'presentation' },
        { text: 'Document', value: 'document' }
      ],
      buyerPersonaOptions: [
        { text: 'Executive', value: 'executive' },
        { text: 'Technical', value: 'technical' },
        { text: 'Procurement', value: 'procurement' },
        { text: 'Finance', value: 'finance' },
        { text: 'Operations', value: 'operations' }
      ],
      industryOptions: [
        'Technology', 'Healthcare', 'Finance', 'Education', 'Retail',
        'Manufacturing', 'Real Estate', 'Transportation', 'Energy', 'Media'
      ]
    };
  },
  async mounted() {
    await this.fetchProposals();
  },
  methods: {
    async fetchProposals() {
      try {
        this.isLoading = true;
        const response = await axios.get('proposals');
        this.proposals = response.data.data;
      } catch (error) {
        console.error('Error fetching proposals:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load proposals. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
    async createProposal() {
      if (!this.$refs.createForm.validate()) return;
      
      try {
        this.creatingProposal = true;
        const response = await axios.post('proposals', this.newProposal);
        this.proposals.unshift(response.data.data);
        this.closeCreateDialog();
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Proposal created successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error creating proposal:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to create proposal. Please try again.']
        });
      } finally {
        this.creatingProposal = false;
      }
    },
    async generatePitch(proposal) {
      try {
        this.generatingPitch = proposal.id;
        await axios.post(`proposals/${proposal.id}/generate`);
        
        // Update proposal status
        const index = this.proposals.findIndex(p => p.id === proposal.id);
        if (index !== -1) {
          this.proposals[index].status = 'generated';
        }
        
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Pitch generated successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error generating pitch:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to generate pitch. Please try again.']
        });
      } finally {
        this.generatingPitch = null;
      }
    },
    async deleteProposal(proposal) {
      if (!confirm(`Are you sure you want to delete the proposal for ${proposal.client_name}?`)) {
        return;
      }
      
      try {
        await axios.delete(`proposals/${proposal.id}`);
        const index = this.proposals.findIndex(p => p.id === proposal.id);
        if (index !== -1) {
          this.proposals.splice(index, 1);
        }
        this.$store.dispatch('settings/showSnackbar', {
          message: 'Proposal deleted successfully!',
          color: 'success'
        });
      } catch (error) {
        console.error('Error deleting proposal:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to delete proposal. Please try again.']
        });
      }
    },
    viewProposal(proposal) {
      this.$router.push(`/agents/pitchcrafter-ai/proposals/${proposal.id}`);
    },
    editProposal(proposal) {
      this.$router.push(`/agents/pitchcrafter-ai/proposals/${proposal.id}/edit`);
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.newProposal = {
        client_name: '',
        client_company: '',
        client_email: '',
        industry: '',
        opportunity_size: '',
        pain_points: '',
        requirements: '',
        buyer_persona: 'executive',
        proposal_type: 'email'
      };
      this.$refs.createForm?.resetValidation();
    },
    applyFilters() {
      // Implement filtering logic if needed
    },
    getStatusColor(status) {
      const colors = {
        draft: 'grey',
        generated: 'blue',
        sent: 'orange',
        accepted: 'green',
        rejected: 'red'
      };
      return colors[status] || 'grey';
    },
    getStatusText(status) {
      const texts = {
        draft: 'Draft',
        generated: 'Generated',
        sent: 'Sent',
        accepted: 'Accepted',
        rejected: 'Rejected'
      };
      return texts[status] || status;
    },
    getTypeColor(type) {
      const colors = {
        email: '#667eea',
        presentation: '#51cf66',
        document: '#ff9500'
      };
      return colors[type] || '#667eea';
    },
    getTypeIcon(type) {
      const icons = {
        email: 'mdi-email',
        presentation: 'mdi-presentation',
        document: 'mdi-file-document'
      };
      return icons[type] || 'mdi-file-document';
    },
    getTypeText(type) {
      const texts = {
        email: 'Email',
        presentation: 'Presentation',
        document: 'Document'
      };
      return texts[type] || type;
    },
    formatCurrency(value) {
      if (!value) return 'Not specified';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.pitchcrafter-proposals {
  min-height: calc(100vh - 64px);
}

.header-section {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.create-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.filters-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.proposals-table {
  border-radius: 16px !important;
}

.client-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.client-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #667eea;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
}

.client-details {
  flex: 1;
}

.client-name {
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.2;
}

.client-company {
  font-size: 12px;
  color: #64748b;
  line-height: 1.2;
}

.status-chip {
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.type-badge {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #2c3e50;
}

.opportunity-value {
  font-weight: 600;
  color: #51cf66;
}

.date-info {
  color: #64748b;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

/* Responsive Design */
@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .page-title {
    font-size: 28px;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .header-section {
    padding: 20px;
  }
  
  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .client-info {
    flex-direction: column;
    gap: 8px;
  }
  
  .client-avatar {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
}
</style>